import React, { useEffect, useState } from "react"
import { Link } from "react-router"
import { useTranslation } from 'react-i18next'
import Select from 'react-select'

import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'
import DocumentStatusHistory from 'pages/parts/DocumentStatusHistory'

import { GET, httpOk } from 'helpers/api'
import { formatDate, formatMoney, returnSign } from "helpers/helpers"

// Возвраты от контрагентов: точка другого логина вернула товар, который мы ей
// отгрузили перемещением. Ожидающие — сверху, их надо принять или отклонить.
function Index() {
	const { t } = useTranslation()
	const date = new Date()

	const statuses = [
		{ 'value': -2, 'label': t('integration_return_status_pending') },
		{ 'value': 1, 'label': t('integration_return_status_accepted') },
		{ 'value': -1, 'label': t('integration_return_status_rejected') },
	]

	const [data, setData] = useState({})
	const [statusHistoryId, setStatusHistoryId] = useState(null)
	const [filterData, setFilterData] = useState({
		'start_date': formatDate(new Date(date.getFullYear(), date.getMonth() - 1, 1), 'yyyy-MM-dd'),
		'end_date': formatDate(new Date(), 'yyyy-MM-dd'),
		'status': null,
		'page': 0,
	})
	const [pagination, setPagination] = useState({ 'url': '', 'response': null })

	async function searchWithFilter(passedFilterData = filterData) {
		var urlParams = ""
		if (passedFilterData.status !== null && passedFilterData.status !== undefined)
			urlParams += returnSign(urlParams) + 'status=' + passedFilterData.status
		if (passedFilterData.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + passedFilterData.start_date
		if (passedFilterData.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + passedFilterData.end_date
		if (passedFilterData.page)
			urlParams += returnSign(urlParams) + 'page=' + passedFilterData.page

		const filterUrl = '/service/returns-in' + urlParams
		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ 'url': filterUrl, 'response': response.data })
		}
	}

	function handleFilter(patch) {
		const filterDataCopy = { ...filterData, ...patch, 'page': 0 }
		setFilterData(filterDataCopy)
		searchWithFilter(filterDataCopy)
	}

	function handlePaginate(page) {
		const filterDataCopy = { ...filterData, 'page': page }
		setFilterData(filterDataCopy)
		searchWithFilter(filterDataCopy)
	}

	function returnClassStatus(status) {
		if (Number(status) === 1) return "bg-success text-white"
		if (Number(status) === -1) return "bg-danger text-white"
		if (Number(status) === -2) return "bg-warning"
		return ""
	}

	function returnStatusName(status) {
		return statuses.find(item => item.value === Number(status))?.label ?? ''
	}

	useEffect(() => {
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('returns_from_counterparty')} [{data.total ?? 0}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div style={{ 'minWidth': '200px' }}>
								<Select
									isClearable
									options={statuses}
									value={statuses.find(option => option.value === filterData.status) || null}
									onChange={(option) => handleFilter({ 'status': option ? option.value : null })}
									placeholder={t('status')}
									noOptionsMessage={() => t('list_empty')}
								/>
							</div>
							<div>
								<PeriodPicker
									startDate={filterData.start_date}
									endDate={filterData.end_date}
									onChange={(period) => handleFilter(period)}
									withTime={false}
									minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
									maxDate={new Date(new Date().getFullYear(), 11, 31)}
								/>
							</div>
						</div>
						<div className="vertical-center">
							<h4><b>{t('amount')}:</b> {formatMoney(data?.total_amount)}</h4>
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">№</th>
									<th style={{ 'width': '20%' }}>{t('counterparty')}</th>
									<th style={{ 'width': '12%' }} className="text-center">{t('return_basis_transfer')}</th>
									<th style={{ 'width': '15%' }}>{t('wms')}</th>
									<th style={{ 'width': '18%' }}>{t('note')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('status')}</th>
									<th style={{ 'width': '10%' }} className="text-center">{t('created')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('amount')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.id}</td>
										<td className="td-ellipsis">
											<Link to={`/service/returns-in/preview/${item.id}`}>{item.source_pos_name}</Link>
										</td>
										<td className="text-center">№{item.wms_transfer_pos_id}</td>
										<td className="td-ellipsis">{item.wms_name}</td>
										<td className="td-ellipsis" title={item.note}>{item.note}</td>
										<td className={`text-center ${returnClassStatus(item.status)}`}
											title={Number(item.status) === -1 ? item.reject_reason : undefined}>
											{returnStatusName(item.status)}
										</td>
										<td className="text-center text-nowrap">
											{formatDate(item.created_at, 'dd.MM.yy HH:mm')}
										</td>
										<td className="text-end text-nowrap">{formatMoney(item.total_amount)}</td>
										<td>
											<div className="d-flex justify-content-end gap-1">
												<Link to={`/service/returns-in/preview/${item.id}`} className="table-action-button table-action-primary-button">
													<i className="uil-eye"></i>
												</Link>
												<div className="table-action-button table-action-primary-button"
													title={t('document_status_history')}
													onClick={() => setStatusHistoryId(item.id)}>
													<i className="uil-history"></i>
												</div>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={handlePaginate}>
						</FilterPagination>
					}
				</div>
			</div>

			<DocumentStatusHistory
				entity="integration_returns"
				documentId={statusHistoryId}
				setDocumentId={setStatusHistoryId}>
			</DocumentStatusHistory>
		</>
	)
}

export default Index
